import { Search } from 'lucide-react'
import { useDeferredValue, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormError } from '@/components/FormError'
import { Loader } from '@/components/Loader'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { accountLink, bothKnow, useAsk, useFindPeople, type Found } from '@/lib/connections'
import { Row } from './Connect'

const LINE: Record<NonNullable<Found['status']>, string> = {
  connected: 'Connected',
  asked: 'Asked',
  asks: 'Asked to connect with you',
}

// People on gatheredfrom you could connect with, by name, and asking each one right there. Only people who can be found
// are listed (worker/connections.ts), and people you already know, marked Connected. A face opens their page.
export function FindPeople({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('')
  const deferred = useDeferredValue(q.trim())
  const found = useFindPeople(deferred, open)
  const ask = useAsk()
  const navigate = useNavigate()
  const people = found.data ?? []
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) return
          onClose()
          setQ('')
          ask.reset()
        }}
      >
        <DialogContent className="flex max-h-[90svh] flex-col gap-4 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Search people</DialogTitle>
            <DialogDescription>
              People on gatheredfrom you could connect with. They still choose whether to accept.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              aria-label="Search people on gatheredfrom"
              placeholder="Search by name"
              autoComplete="off"
              maxLength={80}
              className="rounded-full border-transparent bg-secondary pl-10 shadow-none dark:bg-secondary"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <FormError error={found.error ?? ask.error} />
          <div className="-mx-2 min-h-40 overflow-y-auto">
            {found.isPending ? (
              <Loader label="Finding people" />
            ) : people.length === 0 ? (
              <p className="px-2 py-6 text-center text-muted-foreground">
                {deferred ? `No one called “${deferred}” that you can find.` : 'No one to find yet.'}
              </p>
            ) : (
              <ul aria-label="People on gatheredfrom" className="flex flex-col">
                {people.map((x) => (
                  <Row
                    key={x.id}
                    person={x}
                    line={x.status ? LINE[x.status] : x.mutual.length ? `Knows ${bothKnow(x.mutual)}` : ''}
                    onOpen={() => {
                      onClose()
                      navigate(accountLink(x.id))
                    }}
                    action={
                      x.status === 'connected' || x.status === 'asked' ? null : (
                        <Button
                          size="sm"
                          variant={x.status === 'asks' ? 'default' : 'secondary'}
                          disabled={ask.isPending}
                          onClick={() => ask.mutate({ to: x.id })}
                          aria-label={`${x.status === 'asks' ? 'Accept' : 'Connect with'} ${x.name}`}
                        >
                          {x.status === 'asks' ? 'Accept' : 'Connect'}
                        </Button>
                      )
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
